import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../config/apiConfig';
import { useAuth } from '../contexts/AuthContext';
import { useCaja } from '../contexts/CajaContext';
import './AperturaCajaModal.css';

const AperturaCajaModal = ({ onClose }) => {
  const { auth } = useAuth();
  const { abrirCaja } = useCaja();

  const esCajero = auth?.user?.rol === 'CAJERO';

  const [fechaApertura, setFechaApertura] = useState(new Date());
  const [nombre, setNombre] = useState('');
  const [monto, setMonto] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Cajero selector
  const [cajeros, setCajeros] = useState([]);
  // CAJERO: pre-selecciona su propio id. ENCARGADO: vacío hasta que elija.
  const [idCajeroSeleccionado, setIdCajeroSeleccionado] = useState(
    esCajero ? String(auth.user.id) : ''
  );
  const [loadingCajeros, setLoadingCajeros] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => setFechaApertura(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Cargar lista de cajeros — solo para ENCARGADO
  useEffect(() => {
    if (esCajero) {
      setLoadingCajeros(false); // CAJERO se asigna a sí mismo, no necesita la lista
      return;
    }
    const cargarCajeros = async () => {
      try {
        const res = await fetch(getApiUrl('/caja/cajeros'), {
          headers: { 'Authorization': `Bearer ${auth.token}` }
        });
        if (res.ok) setCajeros(await res.json());
      } catch (err) {
        console.error('Error cargando cajeros:', err);
      } finally {
        setLoadingCajeros(false);
      }
    };
    cargarCajeros();
  }, [auth.token, esCajero]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!nombre.trim()) {
      setError('El nombre de la caja es obligatorio.');
      return;
    }

    if (!idCajeroSeleccionado) {
      setError('Debes seleccionar un cajero para esta caja.');
      return;
    }

    setSubmitting(true);

    const dataApertura = {
      nombre: nombre.trim(),
      fecha_apertura: fechaApertura.toISOString(),
      // Un único monto de apertura para esta caja (piso=1 internamente)
      montos: [{ piso: 1, monto: parseFloat(monto) || 0 }],
      observaciones: observaciones,
    };

    try {
      const response = await fetch(getApiUrl('/caja/abrir'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.token}`,
        },
        body: JSON.stringify(dataApertura),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Error al abrir la caja.');
      }

      const result = await response.json();

      // Solo el ENCARGADO llama a este endpoint — el CAJERO ya queda auto-asignado en el backend
      if (!esCajero) {
        const assignRes = await fetch(getApiUrl(`/caja/cajeros/${idCajeroSeleccionado}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.token}`,
          },
          body: JSON.stringify({ id_caja: result.id_caja }),
        });

        if (!assignRes.ok) {
          const errData = await assignRes.json();
          throw new Error(errData.message || 'Error al asignar cajero.');
        }
      }

      abrirCaja({
        id: result.id_caja,
        nombre: result.nombre,
        fecha_apertura: result.fecha_apertura,
      });
      onClose(true);

    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-content">
        <form onSubmit={handleSubmit}>
          <h2>Apertura de Caja</h2>

          <div className="form-group">
            <label>Nombre de esta Caja *</label>
            <input
              type="text"
              placeholder="Ej: Caja Piso 1, Caja Bar, Caja Principal..."
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>Cajero asignado</label>
            {esCajero ? (
              // CAJERO: muestra su propio nombre en readonly, no puede cambiarlo
              <input
                type="text"
                readOnly
                value={`${auth.user.nombre}${auth.user.apellido ? ' ' + auth.user.apellido : ''} (tú)`}
              />
            ) : loadingCajeros ? (
              <p style={{ color: '#888', margin: 0 }}>Cargando cajeros...</p>
            ) : (
              // ENCARGADO: selector interactivo
              <select
                value={idCajeroSeleccionado}
                onChange={(e) => setIdCajeroSeleccionado(e.target.value)}
                required
              >
                <option value="">— Seleccionar cajero —</option>
                {cajeros.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.nombre} {c.apellido}
                    {c.id_caja_asignada ? ` (ya tiene caja: ${c.nombre_caja})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="form-group">
            <label>Fecha y Hora de Apertura</label>
            <input type="text" readOnly value={fechaApertura.toLocaleString()} />
          </div>

          <div className="form-group">
            <label>Monto de Apertura (S/.)</label>
            <input
              type="number"
              step="0.01"
              placeholder="0.00"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Observaciones</label>
            <textarea
              rows="3"
              placeholder="Anotaciones adicionales..."
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
            />
          </div>

          {error && <p className="error-message">{error}</p>}

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={() => onClose(false)}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Abriendo...' : 'Aperturar Caja'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AperturaCajaModal;
